import React from 'react';
import ReactDOM from 'react-dom';
import './Toast.css';

export type ToastTone = 'success' | 'error' | 'warning' | 'info';

export interface ToastOptions {
  title: React.ReactNode;
  description?: React.ReactNode;
  tone?: ToastTone;
  /** Milliseconds before auto-dismiss. Pass 0 to require manual dismiss. */
  duration?: number;
}

interface ToastEntry extends Required<Pick<ToastOptions, 'tone' | 'duration'>> {
  id: number;
  title: React.ReactNode;
  description?: React.ReactNode;
}

interface ToastContextValue {
  show: (options: ToastOptions) => number;
  dismiss: (id: number) => void;
}

const ToastContext = React.createContext<ToastContextValue | null>(null);

let nextToastId = 1;

export const ToastProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [toasts, setToasts] = React.useState<ToastEntry[]>([]);
  const timers = React.useRef(new Map<number, ReturnType<typeof setTimeout>>());

  const dismiss = React.useCallback((id: number) => {
    const timer = timers.current.get(id);
    if (timer) {
      clearTimeout(timer);
      timers.current.delete(id);
    }
    setToasts((current) => current.filter((t) => t.id !== id));
  }, []);

  const show = React.useCallback(
    (options: ToastOptions) => {
      const id = nextToastId++;
      const entry: ToastEntry = {
        id,
        tone: options.tone ?? 'info',
        duration: options.duration ?? 4500,
        title: options.title,
        description: options.description
      };
      setToasts((current) => [...current, entry]);
      if (entry.duration > 0) {
        const timer = setTimeout(() => dismiss(id), entry.duration);
        timers.current.set(id, timer);
      }
      return id;
    },
    [dismiss]
  );

  React.useEffect(() => {
    const active = timers.current;
    return () => active.forEach(clearTimeout);
  }, []);

  const value = React.useMemo(() => ({ show, dismiss }), [show, dismiss]);

  const viewport =
    typeof document !== 'undefined'
      ? ReactDOM.createPortal(
          <div
            className="bso-toast-viewport"
            role="region"
            aria-label="Notifications"
            aria-live="polite"
          >
            {toasts.map((toast) => (
              <ToastItem key={toast.id} toast={toast} onDismiss={dismiss} />
            ))}
          </div>,
          document.body
        )
      : null;

  return (
    <ToastContext.Provider value={value}>
      {children}
      {viewport}
    </ToastContext.Provider>
  );
};

export const useToast = (): ToastContextValue => {
  const ctx = React.useContext(ToastContext);
  if (!ctx) {
    throw new Error('useToast must be used inside a <ToastProvider>');
  }
  return ctx;
};

const ToastItem: React.FC<{ toast: ToastEntry; onDismiss: (id: number) => void }> = ({
  toast,
  onDismiss
}) => (
  <div className={`bso-toast bso-toast--${toast.tone}`} role={toast.tone === 'error' ? 'alert' : 'status'}>
    <span className="bso-toast__indicator" aria-hidden="true" />
    <div className="bso-toast__content">
      <div className="bso-toast__title">{toast.title}</div>
      {toast.description && (
        <div className="bso-toast__description">{toast.description}</div>
      )}
    </div>
    <button
      type="button"
      className="bso-toast__close"
      aria-label="Dismiss notification"
      onClick={() => onDismiss(toast.id)}
    >
      ×
    </button>
  </div>
);
